import http from "../helpers/http";

export class CompanyService {
    static new() {
        return new CompanyService();
    }

    basePath(placeId) {
        return `/places/${placeId}/plans/companies`;
    }

    list(placeId, params = {}) {
        return http.get(this.basePath(placeId), { params });
    }

    show(placeId, companyId) {
        return http.get(`${this.basePath(placeId)}/${companyId}`);
    }

    /**
     * Cria empresa. Para vincular dados do contrato na criação, envie no data:
     * start_date, valid_until, subs_base_price, subs_dependent_price (usados na geração do contrato inicial).
     */
    store(placeId, data) {
        return http.post(this.basePath(placeId), data);
    }

    update(placeId, companyId, data) {
        return http.put(`${this.basePath(placeId)}/${companyId}`, data);
    }

    destroy(placeId, companyId) {
        return http.delete(`${this.basePath(placeId)}/${companyId}`);
    }

    subscribers(placeId, companyId, params = {}) {
        return http.get(`${this.basePath(placeId)}/${companyId}/subscribers`, { params });
    }

    charges(placeId, companyId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/charges`);
    }

    markChargePaid(placeId, companyId, chargeId, data = {}) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/mark-paid`, data);
    }

    unmarkChargePaid(placeId, companyId, chargeId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/unmark-paid`);
    }

    downloadChargeNfseZip(placeId, companyId, chargeId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/nfse/download-zip`, {
            responseType: 'blob'
        });
    }

    retryChargeNfse(placeId, companyId, chargeId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/nfse/retry`);
    }

    resendChargeEmail(placeId, companyId, chargeId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/resend-email`);
    }

    cancelCharge(placeId, companyId, chargeId, data = {}) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/cancel`, data);
    }

    reprocessBoleto(placeId, companyId, chargeId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/reprocess-boleto`);
    }

    reprocessNfse(placeId, companyId, chargeId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/charges/${chargeId}/reprocess-nfse`);
    }

    downloadContract(placeId, companyId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/contract/download`, {
            responseType: 'blob'
        });
    }

    generateContract(placeId, companyId, data = {}) {
        return http.post(`${this.basePath(placeId)}/${companyId}/contract/generate`, data);
    }

    dashboard(placeId, companyId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/dashboard`);
    }

    cancelSubscriber(placeId, companyId, subscriberId) {
        return http.delete(`${this.basePath(placeId)}/${companyId}/subscribers/${subscriberId}`);
    }

    reactivateSubscriber(placeId, companyId, subscriberId) {
        return http.patch(`${this.basePath(placeId)}/${companyId}/subscribers/${subscriberId}/reactivate`);
    }

    contracts(placeId, companyId, params = {}) {
        return http.get(`${this.basePath(placeId)}/${companyId}/contracts`, { params });
    }

    updateContract(placeId, companyId, contractId, data) {
        return http.put(`${this.basePath(placeId)}/${companyId}/contracts/${contractId}`, data);
    }

    cancelContract(placeId, companyId, contractId, data = {}) {
        return http.post(`${this.basePath(placeId)}/${companyId}/contracts/${contractId}/cancel`, data);
    }

    reopenContract(placeId, companyId, contractId) {
        return http.post(`${this.basePath(placeId)}/${companyId}/contracts/${contractId}/reopen`);
    }

    renewContract(placeId, companyId, contractId, data = {}) {
        return http.post(`${this.basePath(placeId)}/${companyId}/contracts/${contractId}/renew`, data);
    }

    contractRenews(placeId, companyId, contractId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/contracts/${contractId}/renews`);
    }

    downloadEmployeesTemplate(placeId, params = {}) {
        return http.get(`${this.basePath(placeId)}/employees/import/template`, {
            params,
            responseType: 'blob'
        });
    }

    importEmployees(placeId, companyId, file, startDate = null, idContract = null) {
        const formData = new FormData();
        formData.append('file', file);
        if (startDate) formData.append('start_date', startDate);
        if (idContract) formData.append('id_contract', idContract);
        return http.post(`${this.basePath(placeId)}/${companyId}/employees/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    }

    listEmployeesImports(placeId, companyId, params = {}) {
        return http.get(`${this.basePath(placeId)}/${companyId}/employees/imports`, { params });
    }

    showEmployeesImport(placeId, companyId, importId, params = {}) {
        return http.get(`${this.basePath(placeId)}/${companyId}/employees/imports/${importId}`, { params });
    }

    billingClosingInvoice(placeId, companyId, closingId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/billing/closings/${closingId}/invoice`);
    }

    exportBillingClosingInvoicePdf(placeId, companyId, closingId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/billing/closings/${closingId}/invoice/export/pdf`, {
            responseType: 'blob'
        });
    }

    exportBillingClosingInvoiceExcel(placeId, companyId, closingId) {
        return http.get(`${this.basePath(placeId)}/${companyId}/billing/closings/${closingId}/invoice/export/excel`, {
            responseType: 'blob'
        });
    }
}
